import {
  faPlus,
  faSearch,
  faTrashAlt,
} from "@fortawesome/pro-regular-svg-icons";
import { FormState, useFieldArray, useFormContext } from "react-hook-form";
import slugify from "slugify";

import { Button } from "src/base-components/Button";
import { ErrorHint } from "src/base-components/ErrorHint";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { FormStepWrapper } from "src/cases/templates/FormStepWrapper";
import { CaseTemplateFormValues } from "src/cases/templates/caseTemplateFormUtils";
import { getStepById } from "src/cases/templates/formSteps";
import { useIsTemplateVersionPublished } from "src/cases/templates/utils";
import { EmptyState } from "src/design-system/EmptyState";

// Custom validation for duplicate keys
const validateUniqueKey = (
  value: string,
  formValues: CaseTemplateFormValues,
): string | boolean => {
  if (!value) return true; // Allow empty values, handle with required validation

  const allKeys = formValues.attributes.review_roles;
  const duplicates = allKeys.filter((role) => role.key === value);

  return (
    duplicates.length <= 1 ||
    `Role name must transform into a unique key: ${duplicates
      .map((role) => `"${role.display_name}"`)
      .join(", ")} transform into the same key: ${value}`
  );
};

export const AssignReviewRolesStep: React.FC = () => {
  const step = getStepById("assign-review-roles");
  const form = useFormContext<CaseTemplateFormValues>();
  const isPublished = useIsTemplateVersionPublished();
  const { fields, append, remove } = useFieldArray<
    CaseTemplateFormValues,
    "attributes.review_roles",
    "id"
  >({
    name: "attributes.review_roles",
  });

  const addRole = () => {
    append(
      {
        display_name: "",
        description: "",
        key: "",
      },
      {
        focusName: `attributes.review_roles.${fields.length}.display_name`,
      },
    );
  };

  return (
    <FormStepWrapper description={step.description} title={step.title}>
      {fields.length > 0 ? (
        <>
          <div className="grid grid-cols-12 gap-x-2 gap-y-3">
            <ReviewRoleRow
              roleDescription={
                <div className="text-gray-500 font-inter-normal-12px">
                  Description
                </div>
              }
              roleName={
                <div className="text-gray-500 font-inter-normal-12px">
                  Role name
                </div>
              }
            />
            {fields.map((field, index) => {
              return (
                <ReviewRoleRow
                  key={field.id}
                  roleDescription={
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Description"
                        {...form.register(
                          `attributes.review_roles.${index}.description`,
                        )}
                        disabled={isPublished}
                        fullWidth
                      />
                      {!isPublished && (
                        <Icon
                          color="text-gray-500 hover:text-gray-800"
                          icon={faTrashAlt}
                          size="sm"
                          onClick={() => remove(index)}
                        />
                      )}
                    </div>
                  }
                  roleName={
                    <div className="relative">
                      <Input
                        placeholder="Role name"
                        {...form.register(
                          `attributes.review_roles.${index}.display_name`,
                          {
                            required: "Role name is required",
                            onChange: (e) => {
                              const { isDirty } = form.getFieldState(
                                `attributes.review_roles.${index}.key`,
                              );

                              if (!isDirty) {
                                const slug = slugify(e.target.value, {
                                  lower: true,
                                  strict: true,
                                  replacement: "_",
                                });
                                form.setValue(
                                  `attributes.review_roles.${index}.key`,
                                  slug,
                                );
                              }
                            },
                            deps: `attributes.review_roles.${index}.key`,
                          },
                        )}
                        disabled={isPublished}
                        errored={
                          !!getRoleError(form.formState, index)?.display_name ||
                          !!getRoleError(form.formState, index)?.key
                        }
                        fullWidth
                      />
                      {/* Hidden field for key with validation */}
                      <input
                        type="hidden"
                        {...form.register(
                          `attributes.review_roles.${index}.key`,
                          { validate: validateUniqueKey },
                        )}
                      />
                      {getRoleError(form.formState, index)?.display_name && (
                        <ErrorHint>
                          {
                            getRoleError(form.formState, index)?.display_name
                              ?.message
                          }
                        </ErrorHint>
                      )}
                      {getRoleError(form.formState, index)?.key && (
                        <ErrorHint>
                          {getRoleError(form.formState, index)?.key?.message}
                        </ErrorHint>
                      )}
                    </div>
                  }
                />
              );
            })}
          </div>
          {!isPublished && (
            <div className="mt-4">
              <Button
                iconLeft={faPlus}
                size="sm"
                variant="secondary"
                onClick={addRole}
              >
                Add Role
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="h-110">
          <EmptyState
            action={
              !isPublished && (
                <Button
                  iconLeft={faPlus}
                  size="sm"
                  variant="primary"
                  onClick={addRole}
                >
                  Add role
                </Button>
              )
            }
            dataLoc="assign-review-roles-empty-state"
            description="Define the roles involved in the case review process."
            headline="No review roles defined yet"
            icon={faSearch}
          />
        </div>
      )}
    </FormStepWrapper>
  );
};

const ReviewRoleRow: React.FC<{
  roleName: React.ReactNode;
  roleDescription: React.ReactNode;
}> = ({ roleName, roleDescription }) => {
  return (
    <>
      <div className="col-span-3">{roleName}</div>
      <div className="col-span-9">{roleDescription}</div>
    </>
  );
};

const getRoleError = (
  formState: FormState<CaseTemplateFormValues>,
  index: number,
) => {
  return formState.errors.attributes?.review_roles?.[index];
};
